import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Alert,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SpeedIcon from "@mui/icons-material/Speed";
import { payWithPaystack } from "../../utils/paystack";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();

  // =====================================================
  // 🔥 DYNAMIC PLANS ONLY (NO HARDCODE)
  // =====================================================
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadPlans = async () => {
      try {
        const res = await api.get("/subscription/plans");
        if (mounted) {
          setPlans(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Failed to load subscription plans:", err);
        setPlans([]);
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      mounted = false;
    };
  }, []);

  // =====================================================
  // DERIVED PLAN GROUPS
  // =====================================================
  const cbtPlans = plans.filter(
    (p) => (p.scope || "CBT") === "CBT" && !p.is_post_utme_unlimited
  );

  const postUtmePlans = plans.filter(
    (p) => p.scope === "POST_UTME" || p.is_post_utme_unlimited === true
  );

  const mostPopularCbtId =
    cbtPlans.length > 0
      ? [...cbtPlans].sort((a, b) => b.duration_days - a.duration_days)[0].id
      : null;

  // =====================================================
  // PAYSTACK CHECKOUT (UNCHANGED)
  // =====================================================
  const handleSubscribe = async (planId) => {
    if (!user) {
      alert("Please login to subscribe.");
      return;
    }

    try {
      const { data } = await api.post("/payments/init", {
        plan_id: planId,
      });

      const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

      if (!publicKey) {
        alert("Paystack public key not configured");
        return;
      }

      payWithPaystack({
        publicKey,
        email: user.email,
        amount: data.amount,
        reference: data.reference,
        onSuccess: async () => {
          setTimeout(async () => {
            await refreshUser();
            navigate("/dashboard/subjects");
          }, 800);
        },
      });
    } catch (err) {
      console.error("Payment init error:", err);
      alert("Failed to initialize payment. Please try again.");
    }
  };

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <Box>
      {/* HEADER */}
      <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
        💳 Pricing & Subscription Plans
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a plan that fits your exam goals. Secure payments. Instant access.
      </Typography>

      {/* FREE TRIAL */}
      <Alert
        severity="warning"
        sx={{ mb: 4, borderRadius: 2, alignItems: "flex-start" }}
      >
        <Typography fontWeight={700}>🚀 Free Trial Access</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Send this message:
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: "primary.main", mt: 1 }}
        >
          “I just registered, (your registered email), send my free trial”
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          📲 WhatsApp:{" "}
          <strong>
            <a
              href="https://wa.me/2347034733890?text=I%20just%20registered%2C%20send%20my%20free%20trial"
              target="_blank"
              rel="noopener noreferrer"
            >
              Request Free Trial
            </a>
          </strong>
        </Typography>
      </Alert>

      {/* CBT PLANS */}
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        📘 General CBT Plans
      </Typography>

      {loadingPlans ? (
        <Box textAlign="center" py={5}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            Loading plans…
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {cbtPlans.map((plan) => {
            const isPopular = plan.id === mostPopularCbtId;

            return (
              <Grid item xs={12} md={3} key={plan.id}>
                <Paper
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 3,
                    border: isPopular
                      ? "2px solid #1976d2"
                      : "1px solid #eee",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  {isPopular && (
                    <Chip
                      label="Most Popular"
                      color="primary"
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}

                  <Typography fontWeight={800}>{plan.name}</Typography>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {Math.round(plan.duration_days / 30)} Month(s)
                  </Typography>

                  <Typography variant="h4" fontWeight={900} sx={{ mb: 2 }}>
                    ₦{plan.price_naira.toLocaleString()}
                  </Typography>

                  <Button
                    fullWidth
                    size="large"
                    variant={isPopular ? "contained" : "outlined"}
                    startIcon={<CreditCardIcon />}
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    Subscribe Now
                  </Button>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* POST UTME */}
      {postUtmePlans.length > 0 && (
        <>
          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
            🎯 Special Post-UTME Packages
          </Typography>

          <Grid container spacing={3} sx={{ mb: 5 }}>
            {postUtmePlans.map((plan) => (
              <Grid item xs={12} md={3} key={plan.id}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: "2px solid #f57c00",
                    transition: "0.3s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SpeedIcon color="warning" />
                    <Typography fontWeight={800}>{plan.name}</Typography>
                  </Stack>

                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {Math.round(plan.duration_days / 30)} Month(s)
                  </Typography>

                  <Typography variant="h4" fontWeight={900} sx={{ mb: 2 }}>
                    ₦{plan.price_naira.toLocaleString()}
                  </Typography>

                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    color="warning"
                    startIcon={<CreditCardIcon />}
                    onClick={() => handleSubscribe(plan.id)}
                  >
                    Activate Post-UTME
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* WHAT YOU GET */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography fontWeight={700} sx={{ mb: 2 }}>
          📦 What You Get
        </Typography>

        <Grid container spacing={2}>
          {[
            "Unlimited CBT practice",
            "All subjects (JAMB / WAEC / NECO)",
            "Timed exam simulation",
            "Instant scoring & explanations",
            "Performance analytics",
            "Leaderboard access",
            "Mobile & desktop access",
          ].map((item) => (
            <Grid item xs={12} md={6} key={item}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography>{item}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* PAYMENT FLOW */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography fontWeight={700}>🔐 How Payment Works</Typography>
        <Stack spacing={1.2} sx={{ mt: 2 }}>
          <Typography>1. Select a subscription plan</Typography>
          <Typography>2. Choose your payment method</Typography>
          <Typography>3. Pay securely via Paystack</Typography>
          <Typography>4. Subscription activates instantly</Typography>
          <Typography>5. Access expires automatically</Typography>
        </Stack>
      </Paper>

      {/* TERMS */}
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography fontWeight={700}>📌 Important Notes</Typography>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={1}>
          <Typography>• Subscription activates immediately</Typography>
          <Typography>• Payments are non-refundable</Typography>
          <Typography>• One active plan per category</Typography>
          <Typography>• Subscription expires automatically</Typography>
          <Typography>
            • By paying, you agree to CBT Nova terms & conditions
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
